// src/pages/Vendor/Apply/index.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Store, FileText, ClipboardCheck, Award } from 'lucide-react';
import Step1Business from './components/Step1Business';
import Step2Legal from './components/Step2Legal';
import Step3Review from './components/Step3Review';
import Step4Submitted from './components/Step4Submitted';
import { vendorAPI } from '../../../api/vendor';
import toast from 'react-hot-toast';
import { useUser } from '../../../hooks/useUser';
import ApplicationPending from './components/ApplicationPending';
import { useVendor } from '../../../hooks/useVendor';
import VerificationRequired from './components/VerificationRequired';

const VendorApply: React.FC = () => {
  const { user, loading } = useUser();
  const { vendor, loading: vendorLoading } = useVendor();
  const navigate = useNavigate();
  const [isFullyVerified, setIsFullyVerified] = useState(false);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState({
    email: false,
    identity: false,
    phone: false,
  });
  const [formData, setFormData] = useState({
    business_name: '',
    business_email: '',
    business_phone: '',
    vendor_type: '',
    website: '',
    description: '',
    registration_number: '',
    tax_id: '',
    registration_file: null,
    address_proof: null,
    business_address: {
      house_number: '',
      street: '',
      city_id: '',
      city_name: '',
      address_line_2: '',
      captured_location: {
        lat: null,
        lng: null,
      },
    },
  });

  useEffect(() => {
    if (user) {
      setIsFullyVerified(
        user.email_verified &&
        user.is_id_verified &&
        user.phone_verified
      );
      setVerificationStatus({
        email: user.email_verified || false,
        identity: user.is_id_verified || false,
        phone: user.phone_verified || false,
      });
    }
  }, [user]);

  const steps = [
    { number: 1, label: 'Business', icon: Store },
    { number: 2, label: 'Legal & Docs', icon: FileText },
    { number: 3, label: 'Review', icon: ClipboardCheck },
    { number: 4, label: 'Submitted', icon: Award },
  ];

  const handleNext = (data: Partial<typeof formData>) => {
    setFormData({ ...formData, ...data });
    setStep(step + 1);
    setSubmitError(null); // Clear errors when moving forward
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = {
        ...formData,
        business_address: {
          ...formData.business_address,
          latitude: formData.business_address?.captured_location?.lat || null,
          longitude: formData.business_address?.captured_location?.lng || null,
        }
      };
      const response = await vendorAPI.submitApplication(submissionData);

      if (response.error) {
        setSubmitError(response.error);
        toast.error(response.error);
        setIsSubmitting(false);
        return;
      }

      // Success
      setStep(4);
      toast.success('Application submitted successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to submit application';
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isFullyVerified) {
    return (
      <VerificationRequired
        verificationStatus={{
          email: user?.email_verified || false,
          phone: user?.phone_verified || false,
          identity: user?.is_id_verified || false,
        }}
      />
    );
  }

  if (vendor && vendor.verification_status === 'pending') {
    return <ApplicationPending />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Become a Vendor</h1>
          <p className="text-gray-500 mt-1">Join our marketplace and start selling</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 px-4">
          {steps.map((s, index) => {
            const isActive = s.number === step;
            const isCompleted = s.number < step;
            // const Icon = s.icon;

            return (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${isActive
                      ? 'bg-blue-600 text-white'
                      : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    {isCompleted ? <CheckCircle className="h-5 w-5" /> : s.number}
                  </div>
                  <span
                    className={`text-xs mt-1 ${isActive ? 'text-blue-600 font-medium' : 'text-gray-500'
                      }`}
                  >
                    {s.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 ${s.number < step ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          {step === 1 && (
            <Step1Business
              data={formData}
              onNext={handleNext}
            />
          )}
          {step === 2 && (
            <Step2Legal
              data={formData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
          {step === 3 && (
            <Step3Review
              data={formData}
              onBack={handleBack}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              submitError={submitError}
            />
          )}
          {step === 4 && (
            <Step4Submitted />
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorApply;